<template>
  <div
    class="w-[280px] rounded bg-white shadow-normal shadow-gray-400 tablet:w-[300px]"
  >
    <div
      class="border-b border-solid border-gray-400 p-3 text-center tablet:p-4"
    >
      <div
        class="mx-auto mb-1 flex h-9 w-9 items-center justify-center rounded-full border-2 border-solid border-blue-200 bg-blue-100"
      >
        <span
          class="flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold text-blue-400"
        >
          {{ userNameAbbrev }}
        </span>
      </div>
      <p class="text-lg font-bold tablet:text-xl">{{ userName }}</p>
      <p class="mb-1 break-all text-sm text-gray-600">{{ profile.email }}</p>
      <p class="flex justify-center">
        <span
          class="inline-block max-w-full truncate rounded-xl border border-solid border-adv_blue-200 bg-adv_blue-100 px-2 text-xs font-bold text-blue-700"
        >
          {{ userRole }}
        </span>
      </p>

      <div class="mt-4 flex flex-wrap tablet:flex-nowrap tablet:items-center">
        <TheButton
          class="mb-2 w-full tablet:mb-0 tablet:mr-2 tablet:w-3/6"
          :to="editProfilePath"
          :title="$t('header.account.my-account')"
        >
          <span class="text-sm">{{ $t('header.account.my-account') }}</span>
        </TheButton>
        <TheButton
          class="w-full tablet:w-3/6"
          type="outline"
          :title="$t('header.logout')"
          @click="handleLogout"
        >
          <span class="text-sm">{{ $t('header.logout') }}</span>
        </TheButton>
      </div>
    </div>

    <div
      v-if="profile.hasMultipleAccounts"
      class="px-3 py-2.5 text-center tablet:px-4"
    >
      <LayoutHeaderMemberIcon class="mx-auto mb-1 h-9 w-9" />
      <p class="text-lg font-bold tablet:text-xl">
        {{ profile.accountName }}
      </p>
      <p class="break-all text-sm text-gray-600">
        {{ accountCaption }}
      </p>
    </div>

    <ul class="account-list px-3 pb-4 pt-2.5 text-base tablet:px-4">
      <li class="mb-3">
        <TheNuxtLink
          :to="`${consoleDomain}/${locale}/membership`"
          target="_self"
          :title="$t('header.account.my-membership')"
          data-test="header__usermenu-link"
          class="flex justify-between hover:text-blue-400"
        >
          <span class="block truncate font-bold">
            {{ $t('header.account.my-membership') }}
          </span>
          <span class="block truncate pl-1 text-blue-400">
            {{ userMemberType }}
          </span>
        </TheNuxtLink>
      </li>
      <li class="mb-3">
        <TheNuxtLink
          :to="`${consoleDomain}/${locale}/gift-values`"
          target="_self"
          :title="`${$t('header.account.my-gift-value')} ${userBalance}`"
          data-test="header__usermenu-link"
          class="flex justify-between hover:text-blue-400"
        >
          <span class="block truncate font-bold">
            {{ $t('header.account.my-gift-value') }}
          </span>
          <span class="block truncate pl-1 text-blue-400">
            {{ userBalance }}
          </span>
        </TheNuxtLink>
      </li>
      <li class="mb-3 last:mb-0">
        <TheNuxtLink
          :to="`${consoleDomain}/${locale}/bills`"
          target="_self"
          :title="$t('header.account.my-order')"
          data-test="header__usermenu-link"
          class="font-bold hover:text-blue-400"
        >
          {{ $t('header.account.my-order') }}
        </TheNuxtLink>
      </li>
      <li class="mb-3 last:mb-0">
        <TheNuxtLink
          :to="`${consoleDomain}/${locale}/licenses`"
          target="_self"
          :title="$t('header.account.my-license')"
          data-test="header__usermenu-link"
          class="font-bold hover:text-blue-400"
        >
          {{ $t('header.account.my-license') }}
        </TheNuxtLink>
      </li>
      <li v-if="profile.isAdmin" class="mb-3 last:mb-0">
        <TheNuxtLink
          :to="`${consoleDomain}/${locale}/team-management`"
          target="_self"
          :title="$t('header.account.my-team')"
          data-test="header__usermenu-link"
          class="font-bold hover:text-blue-400"
        >
          {{ $t('header.account.my-team') }}
        </TheNuxtLink>
      </li>
      <li v-if="isDesktop && !profile.isSales" class="mb-3 last:mb-0">
        <TheNuxtLink
          id="go-support-center"
          :to="`/${locale}/marketplace/redirect/report-center`"
          :title="$t('header.account.help-center')"
          target="_blank"
          class="font-bold hover:text-blue-400"
        >
          {{ $t('header.account.help-center') }}
        </TheNuxtLink>
      </li>
      <li v-if="profile.hasMultipleAccounts" class="mb-3 last:mb-0">
        <TheNuxtLink
          :to="`${consoleDomain}/${locale}/company-accounts`"
          target="_self"
          :title="$t('header.account.my-customer')"
          data-test="header__usermenu-link"
          class="font-bold hover:text-blue-400"
        >
          {{ $t('header.account.my-customer') }}
        </TheNuxtLink>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
  const runtimeConfig = useRuntimeConfig();
  const { API_DOMAIN } = runtimeConfig.public;
  const { locale } = useI18n();

  const { consoleDomain } = useWebsite();
  const { isDesktop } = useScreen();

  const profileStore = useProfileStore();

  const {
    profile,
    userName,
    userNameAbbrev,
    userRole,
    userMemberType,
    userBalance,
  } = storeToRefs(profileStore);

  const editProfilePath = computed((): string => {
    const callbackUrl = window.location.href;
    return `${API_DOMAIN}/marketplace/api/v1/profile/edit?websiteCode=${locale.value}&callbackUrl=${callbackUrl}`;
  });

  const accountCaption = computed((): string => {
    const { erpId, accountId } = profile.value;
    return `${erpId}${erpId && accountId && '・'}${accountId}`;
  });

  const handleLogout = (): void => {
    profileStore.HANDLE_LOGOUT();
  };
</script>

<style lang="scss" scoped>
  @use '@/assets/scss/_utils.scss' as *;

  .account-list {
    @apply max-h-none overflow-hidden;

    @media (min-width: 1024px) and (max-width: 1400px) {
      @apply max-h-28 overflow-y-auto;
    }
  }
</style>
